import { ColumnDef } from "@tanstack/react-table";
import { SimpleTable } from "@/core/components/simple-table";
import { Button } from "@volca/ui/components/ui/button";
import {
  Empty,
  EmptyHeader,
  EmptyTitle,
  EmptyDescription,
  EmptyContent,
} from "@volca/ui/components/ui/empty";
import { EmptyTableMessage } from "@volca/ui/components/ui/empty-table-message";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import type { ReviewWithSearchableFields } from "@/reviews/review";
import { AttributionCol } from "./cols/attribution-col";
import { DateCol } from "./cols/date-col";
import { PlatformCol } from "./cols/platform-col";
import { RatingCol } from "./cols/rating-col";
import { ReviewBodyCol } from "./cols/review-body-col";
import { ReviewerNameCol } from "./cols/reviewer-name-col";

interface ReviewsTableProps {
  data: ReviewWithSearchableFields[];
  isLoading: boolean;
  isError?: boolean;
  onRetry?: () => void;
  searchValue?: string;
  onClearSearch?: () => void;
  selectedRowId?: string;
  onRowClick?: (row: ReviewWithSearchableFields) => void;
}

export const ReviewsTable = ({
  data,
  isLoading,
  selectedRowId,
  onRowClick,
  isError,
  onRetry,
  searchValue,
  onClearSearch,
}: ReviewsTableProps) => {
  const columns: ColumnDef<ReviewWithSearchableFields>[] = [
    ReviewerNameCol<ReviewWithSearchableFields>(),
    DateCol<ReviewWithSearchableFields>(),
    PlatformCol<ReviewWithSearchableFields>(),
    RatingCol<ReviewWithSearchableFields>(),
    ReviewBodyCol<ReviewWithSearchableFields>(),
    AttributionCol<ReviewWithSearchableFields>(),
  ];

  if (!isLoading && data.length === 0 && (isError || searchValue?.trim())) {
    return (
      <Empty
        className={emptyStateVariants({ layout: "collection" })}
        role={isError ? "alert" : undefined}
      >
        <EmptyHeader>
          <EmptyTitle>
            {isError ? "Couldn't load reviews" : "No reviews match your search"}
          </EmptyTitle>
          <EmptyDescription>
            {isError
              ? "Please try again."
              : "Try another search or clear the search to see all reviews."}
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          {isError
            ? onRetry && (
                <Button variant="outline" size="sm" onClick={onRetry}>
                  Try again
                </Button>
              )
            : onClearSearch && (
                <Button variant="outline" size="sm" onClick={onClearSearch}>
                  Clear search
                </Button>
              )}
        </EmptyContent>
      </Empty>
    );
  }
  if (data.length === 0 && !isLoading) {
    return (
      <div className="mr-5 flex h-full min-h-0 flex-col">
        <EmptyTableMessage>
          <EmptyHeader>
            <EmptyTitle>No five-star reviews yet</EmptyTitle>
            <EmptyDescription>
              Five-star reviews will appear here once customers leave feedback.
            </EmptyDescription>
          </EmptyHeader>
        </EmptyTableMessage>
      </div>
    );
  }

  return (
    <SimpleTable
      data={data}
      columns={columns}
      isLoading={isLoading}
      onRowClick={onRowClick}
      selectedRowId={selectedRowId}
    />
  );
};
